import {
  AspectRatio,
  Box,
  Button,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
  Wrap,
} from "@chakra-ui/react";
import PampaImage from "@raiden/library/components/PampaImage";
import FormStateRHF from "@raiden/library/components/ReactHookForm/FormStateRHF";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useState } from "react";
import { IoPencilOutline } from "react-icons/io5";
import { FormattedMessage, useIntl } from "react-intl";
import HoldersUpdateCoverForm from "../../Holders/UpdateCover/Form";
import HoldersUpdateCoverHandler, {
  holderUpdateCovergetDefaultValues,
} from "../../Holders/UpdateCover/Handler";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Holder").Holder} holder
 *
 * @param {Props} props
 */
function WizardHolderCover({ holder: _holder }) {
  const intl = useIntl();

  const [holder, setHolder] = useState(_holder);

  const {
    isOpen: isOpenConfirmation,
    onOpen: onOpenConfirmation,
    onClose: onCloseConfirmation,
  } = useDisclosure();

  return (
    <Box position="relative" flexGrow={1} w="full" maxW="200px">
      <HoldersUpdateCoverHandler
        holder={holder}
        onSuccess={({ response }) => {
          setHolder(response.data);
          onCloseConfirmation();
        }}
        render={({ form, submit, isLoading, isOpen, onClose, onOpen }) => (
          <>
            <Modal
              isOpen={isOpenConfirmation}
              size="xl"
              onClose={onCloseConfirmation}
              scrollBehavior="inside"
              isCentered>
              <ModalOverlay />

              <ModalContent>
                <ModalCloseButton />

                <ModalHeader>
                  {intl.formatMessage({
                    defaultMessage: "Modification de la photo",
                  })}
                </ModalHeader>

                <ModalBody>
                  <Stack w="full" spacing="1rem">
                    <Text>
                      <FormattedMessage defaultMessage="Êtes-vous sûr de vouloir modifier la photo de l'adhérent ?" />
                    </Text>
                  </Stack>
                </ModalBody>

                <ModalFooter>
                  <Wrap spacing=".5rem">
                    <Button
                      onClick={() => {
                        form.reset(
                          holderUpdateCovergetDefaultValues({ holder }),
                        );
                        onCloseConfirmation();
                      }}>
                      {intl.formatMessage({
                        defaultMessage: "Annuler",
                      })}
                    </Button>

                    <Button
                      isDisabled={isLoading}
                      onClick={() => {
                        submit();
                      }}
                      colorScheme="brandSecondary">
                      {intl.formatMessage({
                        defaultMessage: "Enregistrer",
                      })}
                    </Button>
                  </Wrap>
                </ModalFooter>
              </ModalContent>
            </Modal>

            <AspectRatio
              flexShrink={0}
              ratio={10 / 14}
              w="full"
              backgroundColor="gray.200">
              <PampaImage
                borderTopLeftRadius="6px"
                image={holder?.id_card}
                mode="crop"
                format="default"
                transition="opacity 0.5s ease"
                alt={intl.formatMessage({
                  defaultMessage: "Photo",
                })}
                placeholderSrc={generateFrontPath({
                  id: "internal-assets",
                  parameters: {
                    filePath: "placeholder-user.svg",
                  },
                  includeBasePath: true,
                })}
              />
            </AspectRatio>

            <FormStateRHF
              render={({ isDirty }) => (
                <HoldersUpdateCoverForm
                  isLoading={isLoading}
                  isOpen={isOpen}
                  onClose={() => {
                    onClose();
                    if (isDirty) {
                      onOpenConfirmation();
                    }
                  }}
                />
              )}
            />

            <IconButton
              icon={<Icon as={IoPencilOutline} />}
              onClick={() => {
                onOpen();
              }}
              aria-label={intl.formatMessage({
                defaultMessage: "Modifier la photo",
              })}
              variant="outline"
              colorScheme="brandPrimary"
              backdropFilter="blur(6px)"
              backgroundColor="rgba(255, 255, 255, 0.25)"
              position="absolute"
              bottom=".5rem"
              right=".5rem"
            />
          </>
        )}
      />
    </Box>
  );
}

export default WizardHolderCover;
